'use client';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useFormatter, useTranslations } from 'next-intl';
import { useState } from 'react';
import type { FuturesPair } from '@/lib/api/futures/client';
import { useSearchPairs } from '@/lib/api/futures/hooks';
import { ZoneLink } from '@/lib/components/ZoneLink';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { cn } from '@/lib/ui-kit/utils';
import { SearchInput } from '@/lib/ui-kit-v2/search-input';
import {
  CHANGE_DIRECTION_TEXT,
  changeDirection,
  filterMarketPairs,
  formatPairChange,
  formatPairPrice,
  type PairMarket,
} from '@/lib/utils/pairFormat';
import { MARKET_LIST_CLASS, MARKET_ROW_CLASS, PAIR_ICON_SIZE } from './navStyles';

interface FuturesPairsProps {
  market: PairMarket;
  /** Overrides the column's 300px width. */
  className?: string;
}

interface MarketRowProps {
  pair: FuturesPair;
}

interface PairListProps {
  pairs: FuturesPair[] | undefined;
  isLoading: boolean;
}

const SKELETON_ROWS = 6;

// ── Parts ──

function MarketRow({ pair }: MarketRowProps) {
  const t = useTranslations('search');
  const format = useFormatter();

  return (
    <li>
      <NavigationMenu.Link asChild>
        <ZoneLink href={pair.path} className={MARKET_ROW_CLASS}>
          <TokenIcon
            symbol={pair.baseCurrency}
            iconUrl={pair.icon}
            size={PAIR_ICON_SIZE}
            fallback="initials"
            className="shrink-0 rounded-full"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[14px] font-medium leading-5 text-v2-text-primary">
              {pair.symbol}
            </span>
            <span className="text-[12px] leading-[17px] text-v2-text-secondary">
              {t('perpetual')}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">
            <span className="text-[14px] font-medium leading-5 text-v2-text-primary">
              {formatPairPrice(format, pair)}
            </span>
            <span
              className={cn(
                'text-[12px] leading-[17px]',
                CHANGE_DIRECTION_TEXT[changeDirection(pair.changePct)],
              )}
            >
              {formatPairChange(format, pair)}
            </span>
          </span>
        </ZoneLink>
      </NavigationMenu.Link>
    </li>
  );
}

function SkeletonRow() {
  return (
    <li aria-hidden className="flex h-14 shrink-0 items-center gap-3 px-3">
      <Skeleton className="size-7 shrink-0 rounded-full" />
      <span className="flex flex-1 flex-col gap-2">
        <Skeleton className="h-3 w-20 rounded-full" />
        <Skeleton className="h-2.5 w-14 rounded-full" />
      </span>
      <span className="flex flex-col items-end gap-2">
        <Skeleton className="h-3 w-14 rounded-full" />
        <Skeleton className="h-2.5 w-10 rounded-full" />
      </span>
    </li>
  );
}

/** A loading market column's placeholder rows. */
export function MarketListSkeleton() {
  return Array.from({ length: SKELETON_ROWS }, (_, i) => <SkeletonRow key={i} />);
}

function PairList({ pairs, isLoading }: PairListProps) {
  const t = useTranslations('pairSelect');

  if (isLoading) return <MarketListSkeleton />;
  if (!pairs?.length) {
    return (
      <li className="py-6 text-center text-[13px] text-v2-text-secondary">{t('noResults')}</li>
    );
  }
  return pairs.map((pair) => <MarketRow key={pair.id} pair={pair} />);
}

/**
 * The Futures and TradFi mega menus' market column: the header search's pair
 * list (same query and cache), narrowed to the picked market and the typed text.
 * `HeaderNav` keeps its prices live while the menu is open.
 */
export function FuturesPairs({ market, className }: FuturesPairsProps) {
  const [query, setQuery] = useState('');
  const t = useTranslations('search');
  const { data: pairs, isLoading } = useSearchPairs();
  const visiblePairs = pairs ? filterMarketPairs(pairs, market, query.trim()) : undefined;

  return (
    <div className={cn('flex w-[300px] shrink-0 flex-col gap-2', className)}>
      <SearchInput value={query} onChange={setQuery} placeholder={t('placeholder')} />
      <ul className={MARKET_LIST_CLASS}>
        <PairList pairs={visiblePairs} isLoading={isLoading} />
      </ul>
    </div>
  );
}
